import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RuleFactoryType } from "@repo/server-api/types";
import { MeProvider, NotificationProvider, RolesProvider } from "@repo/shared/components";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import Header from "./Header";
import { MenuItem } from "./MenuItem";
import Sidebar from "./Sidebar";
import MuiXLicense from "../shared/MuiXLicense";

interface LayoutProps {
	children: ReactNode;
	menuItems: MenuItem[];
	ruleFactory: RuleFactoryType;
}

const Layout: React.FC<LayoutProps> = ({ children, menuItems, ruleFactory }) => {
	const [mobileOpen, setMobileOpen] = useState(false);
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const appBarRef = useRef<HTMLDivElement>(null);
	const [appBarHeight, setAppBarHeight] = useState(0);

	useEffect(() => {
		let observer: ResizeObserver | null = null;

		if (appBarRef.current) {
			observer = new ResizeObserver((entries) => {
				const entry = entries[0];
				setAppBarHeight(entry.contentRect.height);
			});

			observer.observe(appBarRef.current);
		}

		return () => {
			if (observer) {
				observer.disconnect();
			}
		};
	}, []);

	return (
		<MeProvider>
			<RolesProvider>
				<NotificationProvider>
					{/* Header */}
					<Header
						ref={appBarRef}
						isDesktop={isDesktop}
						onMenuToggle={handleDrawerToggle}
						menuToggleValue={mobileOpen}
					/>

					<Sidebar
						variant={mobileOpen ? "permanent" : "temporary"}
						open={mobileOpen}
						onClose={handleDrawerToggle}
						menuData={menuItems}
						ruleFactory={ruleFactory}
					/>

					<Box
						sx={{
							flexGrow: mobileOpen ? 1 : 0,
							flexShrink: mobileOpen ? 1 : 0,
							padding: theme.spacing(1),
							marginTop: `${appBarHeight}px`,
							marginLeft: mobileOpen ? theme.sidebar.width : "0px",
						}}>
						{children}
					</Box>
					<MuiXLicense />
				</NotificationProvider>
			</RolesProvider>
		</MeProvider>
	);
};

export default Layout;
